import React, { useEffect, useMemo, useState } from "react";
import Layout from "layouts/layout";
import Table from "components/table/table";
import ListContainer from "components/list/list-container";
import ListHeader from "components/list/list-header";
import _ from "lodash";
import { useDialogHook } from "utils/customhooks";
import AddFeaturedProfileModal from "./components/add-featured-profile";
import DeleteFeaturedProfileModal from "./components/delete-featured-profile";
import EditFeaturedProfileModal from "./components/edit-featured-profile";
import ActionIconBtn from "components/button/action-btn";
import EditPencilIconSVG from "assets/svg/edit-pencil-icon";
import TrashIconSVG from "assets/svg/trash-icon";

export default function FeaturedProfilesPage() {
  const { http } = global.services;
  const modal = useDialogHook(AddFeaturedProfileModal);
  const editModal = useDialogHook(EditFeaturedProfileModal);
  const deleteModal = useDialogHook(DeleteFeaturedProfileModal);

  const [state, setState] = useState({
    loading: false,
    page: 1,
    limit: 10,
    pages: [],
    items: [],
  imgVersion: 0,
  });

  const columns = useMemo(
    () => [
      { title: "ID", dataIndex: "id", key: "id", nowrap: true },
      {
        title: "Profile",
        key: "profile",
        nowrap: true,
        render: (record) => {
          const p = record?.user_profile;
          const name = p?.profile_name || p?.username || record?.user_profile_id || "--";
          return <span>{name}</span>;
        },
      },
      {
        title: "Image",
        key: "image",
        nowrap: true,
        render: (record) => {
          const url = record?.media?.webp_url || record?.media?.url || record?.user_profile?.cover_image_url;
          const versioned = url ? `${url}${url.includes("?") ? "&" : "?"}v=${state.imgVersion}` : null;
          return (
            <div className="flex items-center justify-start">
              {versioned ? (
                <img className="w-16 h-9 object-cover rounded" src={versioned} alt="Featured" key={versioned} />
              ) : (
                <span className="text-xs opacity-60">--</span>
              )}
            </div>
          );
        },
      },
      {
        title: "Action",
        key: "action",
        hideSorting: true,
        nowrap: true,
        render: (record) => (
          <>
            <ActionIconBtn
              icon={<EditPencilIconSVG className="m-auto" />}
              onClick={() => handleEdit(record)}
            />
            <ActionIconBtn
              hoverColor="bg-opacity-75"
              className="action-btn bg-red"
              icon={<TrashIconSVG className="m-auto" fill="white" />}
              onClick={() => handleDelete(record)}
            />
          </>
        ),
      },
  ],
  [state.imgVersion]
  );

  const fetchList = async (selectedPage = 1) => {
    if (state.loading) return;
    setState((p) => ({ ...p, loading: true }));
    try {
      const { data } = await http.get("user-profile-selections", {
        page: selectedPage,
        limit: state.limit,
      });
      const list = Array.isArray(data) ? data : (data?.data || data?.items || []);
      const current = Array.isArray(data) ? 1 : (data?.current_page || selectedPage);
      const last = Array.isArray(data)
        ? 1
        : (data?.last_page || Math.ceil((data?.total || list.length) / state.limit) || 1);
      const pages = Array.from({ length: last }, (_, i) => i + 1);
      setState((p) => ({ ...p, items: list, page: current, pages }));
    } catch (err) {
      console.log("error@fetchList", err);
    } finally {
      setState((p) => ({ ...p, loading: false }));
    }
  };

  useEffect(() => {
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAdd = () => {
    modal({}, (result) => {
      if (!_.isEmpty(result)) {
        if (result === "addOrUpdate") {
          setState((p) => ({ ...p, imgVersion: (p.imgVersion || 0) + 1 }));
          fetchList(state.page);
        }
      }
    });
  };

  function handleEdit(record = {}) {
  const payload = { ...record, id: record?.id || record?.user_profile_selection_id };
  editModal(payload, (result) => {
      if (!_.isEmpty(result)) {
        if (result === "addOrUpdate") {
          setState((p) => ({ ...p, imgVersion: (p.imgVersion || 0) + 1 }));
          fetchList(state.page);
        }
      }
    });
  }

  function handleDelete(record = {}) {
    const payload = { ...record, id: record?.id || record?.user_profile_selection_id };
    deleteModal(payload, (result) => {
      if (!_.isEmpty(result)) {
        if (result === "delete") fetchList(state.page);
      }
    });
  }

  return (
    <Layout title="Featured Profiles" routeName="featured-profiles">
      <div className="main-page-wrapper">
        <ListContainer
          header={
            <ListHeader
              title="List"
              buttonName="+ Add Featured"
              countValue={state.items.length}
              showAddButton
              onClickButton={handleAdd}
            />
          }
        >
          <Table
            columns={columns}
            dataSource={state.items}
            pagination
            perPage={state.limit}
            currentPage={state.page}
            pages={state.pages}
            onHandleChangePage={(p) => fetchList(p)}
            loading={state.loading}
          />
        </ListContainer>
      </div>
    </Layout>
  );
}
